import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { format } from "date-fns";
import Reveal from "@/components/store/Reveal";
import api, { mediaUrl } from "@/lib/api";

const fmt = (d) => { try { return format(new Date(d), "MMMM yyyy"); } catch { return ""; } };

export default function Journal() {
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/blog/tags").then(({ data }) => setTags(data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/blog/posts", { params: { tag, q } }).then(({ data }) => setPosts(data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [tag, q]);

  const [feature, ...rest] = posts;

  return (
    <div className="px-6 md:px-12 py-14" data-testid="journal-page">
      <div className="text-center mb-12">
        <p className="eyebrow mb-3">The Journal</p>
        <h1 className="font-heading text-4xl md:text-6xl tracking-tight">Stories from the Voyage</h1>
        <p className="font-body text-sm text-ink/60 max-w-lg mx-auto mt-5 leading-relaxed">Fabric, craft, travel and the quiet philosophy behind conscious resort wear.</p>
      </div>

      <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
        <div className="flex flex-wrap gap-2" data-testid="journal-tags">
          <button onClick={() => setTag("")} className={`px-4 py-1.5 font-body uppercase tracking-wide2 text-[0.6rem] border transition-colors ${!tag ? "bg-ink text-linen border-ink" : "border-ink/20 text-ink/60 hover:border-ink"}`} data-testid="journal-tag-all">All</button>
          {tags.map((t) => (
            <button key={t} onClick={() => setTag(t)} className={`px-4 py-1.5 font-body uppercase tracking-wide2 text-[0.6rem] border transition-colors ${tag === t ? "bg-ink text-linen border-ink" : "border-ink/20 text-ink/60 hover:border-ink"}`} data-testid={`journal-tag-${t}`}>{t}</button>
          ))}
        </div>
        <div className="relative w-full md:w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stories…" data-testid="journal-search" className="w-full bg-transparent border border-ink/20 pl-9 pr-3 py-2 font-body text-sm outline-none focus:border-ink transition-colors" />
        </div>
      </div>

      {posts.length === 0 && <p className="text-center font-body text-sm text-ink/40 py-16" data-testid="journal-empty">No stories found.</p>}

      {feature && !tag && !q && (
        <Reveal className="max-w-6xl mx-auto mb-20">
          <Link to={`/journal/${feature.slug}`} className="group grid md:grid-cols-2 gap-8 items-center" data-testid="journal-featured">
            <div className="overflow-hidden bg-cream aspect-[4/3]"><img src={mediaUrl(feature.cover_image)} alt={feature.cover_alt || feature.title} className="w-full h-full object-cover vintage-photo transition-transform duration-700 group-hover:scale-105" /></div>
            <div>
              <p className="eyebrow mb-3">{feature.tags?.[0]} · Featured</p>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight mb-4 group-hover:italic transition-all">{feature.title}</h2>
              <p className="font-body text-ink/70 leading-relaxed mb-4">{feature.excerpt}</p>
              <p className="font-body text-xs text-ink/40 uppercase tracking-wide2">{fmt(feature.published_at)} · {feature.read_minutes} min read</p>
            </div>
          </Link>
        </Reveal>
      )}

      <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-x-8 gap-y-14">
        {(feature && !tag && !q ? rest : posts).map((p, i) => (
          <Reveal key={p.slug} delay={(i % 3) * 80}>
            <Link to={`/journal/${p.slug}`} className="group block" data-testid={`journal-card-${p.slug}`}>
              <div className="overflow-hidden bg-cream aspect-[4/3] mb-5"><img src={mediaUrl(p.cover_image)} alt={p.cover_alt || p.title} loading="lazy" className="w-full h-full object-cover vintage-photo transition-transform duration-700 group-hover:scale-105" /></div>
              <p className="eyebrow mb-2">{p.tags?.[0]}</p>
              <h3 className="font-heading text-xl tracking-tight mb-2 group-hover:italic transition-all">{p.title}</h3>
              <p className="font-body text-sm text-ink/60 leading-relaxed mb-3 line-clamp-2">{p.excerpt}</p>
              <p className="font-body text-[0.65rem] text-ink/40 uppercase tracking-wide2">{fmt(p.published_at)} · {p.read_minutes} min</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
